import { type JudgeOutcome, type JudgeRootCause } from '../../services/api/judge';

export const BAD_OUTCOMES: readonly JudgeOutcome[] = ['miss', 'unnecessary_handoff', 'abandoned'];

export const OUTCOME_LABELS: Record<JudgeOutcome, string> = {
  resolved: 'Löst',
  necessary_handoff: 'Nödvändig överlämning',
  unnecessary_handoff: 'Onödig överlämning',
  miss: 'Miss',
  abandoned: 'Avbruten',
  not_applicable: 'Ej tillämplig',
};

export const OUTCOME_BAR_COLORS: Record<JudgeOutcome, string> = {
  resolved: 'bg-emerald-500',
  necessary_handoff: 'bg-sky-500',
  unnecessary_handoff: 'bg-amber-500',
  miss: 'bg-rose-500',
  abandoned: 'bg-orange-400',
  not_applicable: 'bg-slate-300',
};

export const OUTCOME_TEXT_COLORS: Record<JudgeOutcome, string> = {
  resolved: 'text-emerald-700',
  necessary_handoff: 'text-sky-700',
  unnecessary_handoff: 'text-amber-700',
  miss: 'text-rose-700',
  abandoned: 'text-orange-700',
  not_applicable: 'text-slate-600',
};

export const OUTCOME_BADGE_COLORS: Record<JudgeOutcome, string> = {
  resolved: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  necessary_handoff: 'border-sky-200 bg-sky-50 text-sky-700',
  unnecessary_handoff: 'border-amber-200 bg-amber-50 text-amber-800',
  miss: 'border-rose-200 bg-rose-50 text-rose-700',
  abandoned: 'border-orange-200 bg-orange-50 text-orange-700',
  not_applicable: 'border-slate-200 bg-slate-50 text-slate-600',
};

export const ROOT_CAUSE_LABELS: Record<JudgeRootCause, string> = {
  knowledge_missing: 'Kunskap saknas',
  knowledge_ignored: 'Kunskap ignorerad',
  search_miss: 'Sökmiss',
  tool_result_ignored: 'Verktygssvar ignorerat',
  wrong_information: 'Fel information',
  prompt_behavior: 'Promptbeteende',
  product_data_missing: 'Produktdata saknas',
  flow_friction: 'Friktion i flödet',
  needs_human: 'Kräver människa',
};

export const INTENT_LABELS: Record<string, string> = {
  order_status: 'Orderstatus',
  cancel_change: 'Avbeställa/ändra',
  return: 'Retur',
  restock: 'Påfyllnad',
  size: 'Storlek',
  product_question: 'Produktfråga',
  styling: 'Stilråd',
  price_discount: 'Pris/rabatt',
  checkout: 'Kassan',
  delivery_pre_purchase: 'Leverans före köp',
  contact: 'Kontakt',
  loyalty: 'Lojalitet',
  other: 'Övrigt',
};

const isKey = <K extends string>(labels: Record<K, string>, value: string): value is K =>
  Object.prototype.hasOwnProperty.call(labels, value);

// Domen kan bära ett värde som en äldre klient inte känner till. Då visas det
// råa värdet hellre än ingenting.
export const outcomeLabel = (value: string | null): string =>
  value === null ? '–' : isKey(OUTCOME_LABELS, value) ? OUTCOME_LABELS[value] : value;

export const rootCauseLabel = (value: string | null): string =>
  value === null ? '–' : isKey(ROOT_CAUSE_LABELS, value) ? ROOT_CAUSE_LABELS[value] : value;

export const intentLabel = (value: string | null): string =>
  value === null ? '–' : isKey(INTENT_LABELS, value) ? INTENT_LABELS[value] : value;

export const outcomeBadgeColor = (value: string | null): string =>
  value !== null && isKey(OUTCOME_BADGE_COLORS, value)
    ? OUTCOME_BADGE_COLORS[value]
    : 'border-slate-200 bg-slate-50 text-slate-600';
